/**
 * @file useExerciseVideo.ts
 *
 * @description Custom hook for managing exercise video URLs in Miracle Flow.
 * Provides functionality to load, save, and delete exercise video URLs.
 *
 * @module miracle-flow
 *
 * @domain productivity
 *
 */

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

/**
 * Exercise video data structure
 */
interface ExerciseVideoData {
  videoUrl: string;
}

/**
 * useExerciseVideo - Hook for managing exercise video URLs
 *
 * Features:
 * - Load video URL from backend
 * - Save video URL to backend
 * - Delete video URL
 * - Automatic loading on mount
 * - Loading and error states
 *
 * @returns Object containing video data, loading state, error, and actions
 *
 * @example
 * ```tsx
 * const { videoData, isLoading, error, saveVideoUrl, deleteVideoUrl } = useExerciseVideo();
 * ```
 *
 */
export function useExerciseVideo() {
  const [videoData, setVideoData] = useState<ExerciseVideoData | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Load video URL from backend
  const loadVideoUrl = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const data = await core.invoke<ExerciseVideoData | null>("load_exercise_video_url");
      setVideoData(data ?? undefined);
      return data;
    } catch (error_: unknown) {
      setError(error_ instanceof Error ? error_.message : "Failed to load video URL");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save video URL to backend
  const saveVideoUrl = useCallback(async (videoUrl: string) => {
    setIsLoading(true);
    setError(undefined);
    try {
      await core.invoke("save_exercise_video_url", { videoData: { videoUrl } });
      setVideoData({ videoUrl });
    } catch (error_: unknown) {
      setError(error_ instanceof Error ? error_.message : "Failed to save video URL");
      throw error_;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete video URL from backend
  const deleteVideoUrl = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      await core.invoke("delete_exercise_video_url");
      setVideoData(undefined);
    } catch (error_: unknown) {
      setError(error_ instanceof Error ? error_.message : "Failed to delete video URL");
      throw error_;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Automatically load video URL when component mounts
  useEffect(() => {
    void loadVideoUrl();
  }, [loadVideoUrl]);

  return {
    deleteVideoUrl,
    error,
    isLoading,
    loadVideoUrl,
    saveVideoUrl,
    videoData,
  };
}
